import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { wabaApi } from '@/api/endpoints/waba'
import { queryKeys } from '@/api/query-keys'
import { useAuthStore } from '@/stores/authStore'
import { DEFAULT_COMPANY_CURRENCY, setCompanyCurrency } from '@/lib/currency'
import type { Company, CompanyRegion } from '@/types'

export const DEFAULT_COMPANY_REGION: CompanyRegion = 'IN'

// Shares the same query key/fetch as useCompanyTimezone so the company
// profile is only requested once regardless of which hook mounts first.
export function useCompanyProfile(): Company | undefined {
  const user = useAuthStore((s) => s.user)

  const companyId = typeof user?.company === 'object'
    ? (user?.company as Company)?.id
    : user?.company
  const cid = companyId ? String(companyId) : ''

  const { data } = useQuery({
    queryKey: queryKeys.company(cid),
    queryFn: () => wabaApi.getCompany(cid),
    enabled: !!cid,
    staleTime: 10 * 60 * 1000,
  })

  return data ?? (typeof user?.company === 'object' ? (user.company as Company) : undefined)
}

/**
 * Returns the company's billing currency (default: INR).
 * Also syncs the active currency used by the formatMoney helper.
 */
export function useCompanyCurrency(): string {
  const company = useCompanyProfile()
  const currency = company?.currency || DEFAULT_COMPANY_CURRENCY

  useEffect(() => {
    setCompanyCurrency(currency)
  }, [currency])

  return currency
}

/**
 * Returns the company's region ("IN" | "AE" | "ZA", default: IN).
 * Drives region-conditional UI such as tax-id fields on the billing profile.
 */
export function useCompanyRegion(): CompanyRegion {
  const company = useCompanyProfile()
  return company?.region || DEFAULT_COMPANY_REGION
}
